import { PayloadAction } from '@reduxjs/toolkit';
import { call, put, select, takeLatest } from 'redux-saga/effects';
import {
  fetchAgentCollectionPercentage,
  fetchAgentCollectionPercentageFailure,
  fetchAgentCollectionPercentageSuccess,
  fetchAgentVisitPercentage,
  fetchAgentVisitPercentageFailure,
  fetchAgentVisitPercentageSuccess,
  fetchAgentWiseSummary,
  fetchAgentWiseSummaryFailure,
  fetchAgentWiseSummarySuccess,
  fetchDayWiseVisit,
  fetchDayWiseVisitFailure,
  fetchDayWiseVisitSuccess,
} from 'redux/slice/field-supervisor/FieldSupervisorVisitSummary.slice';
import { RootState } from 'redux/store/store';
import {
  AgentCollectionPercentageRequest,
  AgentCollectionPercentageResponse,
  AgentVisitPercentageRequest,
  AgentVisitPercentageResponse,
  AgentWiseSummaryRequest,
  AgentWiseSummaryResponse,
  DayWiseVisitRequest,
  DayWiseVisitResponse,
} from 'redux/types/FieldSupervisorVisitSummary.types';
import { fieldSupervisorDashboardEndpoints } from 'services/api/endPoints';
import { HTTPClient } from 'services/api/http-client';

function* handleFetchAgentWiseSummary(
  action: PayloadAction<AgentWiseSummaryRequest>
) {
  try {
    const agencyId: string = yield select(
      (state: RootState) => state.userConfiguration.agencyId || ''
    );
    const response: AgentWiseSummaryResponse = yield call(
      HTTPClient.PostRequest,
      fieldSupervisorDashboardEndpoints.AGENT_WISE_SUMMARY,
      action.payload,
      { headers: { 'agency-id': agencyId } }
    );
    if (response.status === 200) {
      yield put(fetchAgentWiseSummarySuccess(response.data));
    } else {
      yield put(
        fetchAgentWiseSummaryFailure('Error fetching agent wise summary')
      );
    }
  } catch (error) {
    yield put(fetchAgentWiseSummaryFailure(error));
  }
}

function* handleFetchDayWiseVisit(action: PayloadAction<DayWiseVisitRequest>) {
  try {
    const agencyId: string = yield select(
      (state: RootState) => state.userConfiguration.agencyId || ''
    );
    const response: DayWiseVisitResponse = yield call(
      HTTPClient.PostRequest,
      fieldSupervisorDashboardEndpoints.DAY_WISE_VISIT_SUMMARY,
      action.payload,
      { headers: { 'agency-id': agencyId } }
    );

    if (response.status === 200) {
      yield put(fetchDayWiseVisitSuccess(response.data));
    } else {
      yield put(
        fetchDayWiseVisitFailure('Error fetching day wise visit summary')
      );
    }
  } catch (error) {
    yield put(fetchDayWiseVisitFailure(error));
  }
}

function* handleFetchAgentVisitPercentage(
  action: PayloadAction<AgentVisitPercentageRequest>
) {
  try {
    const agencyId: string = yield select(
      (state: RootState) => state.userConfiguration.agencyId || ''
    );
    const response: AgentVisitPercentageResponse = yield call(
      HTTPClient.PostRequest,
      fieldSupervisorDashboardEndpoints.AGENT_VISIT_PERCENTAGE,
      action.payload,
      { headers: { 'agency-id': agencyId } }
    );

    if (response.status === 200) {
      yield put(fetchAgentVisitPercentageSuccess(response.data));
    } else {
      yield put(
        fetchAgentVisitPercentageFailure(
          'Error fetching agent visit percentage'
        )
      );
    }
  } catch (error) {
    yield put(fetchAgentVisitPercentageFailure(error));
  }
}

function* handleFetchAgentCollectionPercentage(
  action: PayloadAction<AgentCollectionPercentageRequest>
) {
  try {
    const response: AgentCollectionPercentageResponse = yield call(
      HTTPClient.PostRequest,
      fieldSupervisorDashboardEndpoints.AGENT_COLLECTION_PERCENTAGE,
      action.payload
    );

    if (response.status === 200) {
      yield put(fetchAgentCollectionPercentageSuccess(response.data));
    } else {
      yield put(
        fetchAgentCollectionPercentageFailure(
          'Error fetching agent collection percentage'
        )
      );
    }
  } catch (error) {
    yield put(fetchAgentCollectionPercentageFailure(error));
  }
}

export function* watchFieldSupervisorVisitSummary() {
  yield takeLatest(fetchAgentWiseSummary.type, handleFetchAgentWiseSummary);
  yield takeLatest(fetchDayWiseVisit.type, handleFetchDayWiseVisit);
  yield takeLatest(
    fetchAgentVisitPercentage.type,
    handleFetchAgentVisitPercentage
  );
  yield takeLatest(
    fetchAgentCollectionPercentage.type,
    handleFetchAgentCollectionPercentage
  );
}
